import { Link } from 'react-router-dom'
import { MAX_RUN_MS, QUEUE_PATH } from '@config/queueConfig'
import { fmtDuration } from './queueFormat'
import { InfoIcon } from './queueIcons'

/**
 * Screen 5A — the in-page handoff, shown once a foreground run passes
 * QUEUE_HANDOFF_MS.
 *
 * It REPLACES the results/loading area rather than stacking above it, so the
 * page never grows a second waiting state. There is at most one of these per
 * page — the caller renders it instead of the spinner block, never alongside.
 *
 * The whole point is better information than the old bare spinner: percent,
 * elapsed, the honest ceiling, and what this report typically returns.
 */
const QueueHandoffPanel = ({
  percent = 0,
  message,
  elapsedMs = 0,
  jobId,
  onNotifyMe,
  onStayAndWatch,
  onDismissRun,
  typicalHint,
  notifyArmed = false,
}) => {
  const pct = Math.min(100, Math.max(0, Math.round(percent)))
  const seconds = Math.round(elapsedMs / 1000)
  const capMinutes = Math.round(MAX_RUN_MS / 60000)
  // Circumference of the r=32 ring, so the dash offset tracks percent exactly.
  const circumference = 2 * Math.PI * 32

  return (
    <div className="overflow-hidden rounded-xl border border-cb-accent/30 bg-white shadow-sm">
      <div className="bg-gradient-to-b from-[#f5faff] to-white px-6 py-7 sm:px-8">
        <div className="flex flex-wrap items-start gap-5">
          <div className="relative h-[76px] w-[76px] shrink-0">
            <svg width="76" height="76" className="-rotate-90" aria-hidden="true">
              <circle cx="38" cy="38" r="32" fill="none" stroke="#e2ecf9" strokeWidth="7" />
              <circle
                cx="38" cy="38" r="32" fill="none"
                stroke="var(--color-cb-accent)" strokeWidth="7" strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - pct / 100)}
                className="transition-all"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-base font-bold tabular-nums text-cb-accent-text">
              {pct}%
            </div>
          </div>

          <div className="min-w-[300px] flex-1">
            <h2 className="mb-1.5 font-display text-xl text-cb-navy">Still running — we moved it to your queue</h2>
            <p className="max-w-[62ch] text-[13px] leading-relaxed text-gray-600">
              This report has been going for {seconds} seconds, so it&apos;s now a background job.{' '}
              <b className="text-cb-navy">You can leave this page or close the tab</b> — it keeps running, and the
              finished report will be waiting in your queue.
            </p>

            <div
              role="progressbar"
              aria-label="Report progress"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              className="mt-3.5 h-1.5 max-w-[400px] overflow-hidden rounded-full bg-gray-100"
            >
              <div className="h-full rounded-full bg-cb-accent transition-all" style={{ width: `${pct}%` }} />
            </div>

            {/* Live region so a screen-reader user hears progress without the
                1.5s poll interrupting them — polite, never assertive. */}
            <div aria-live="polite" className="mt-2.5 flex flex-wrap gap-3.5 text-[12.2px] text-gray-600">
              <span>{message || 'Working…'}</span>
              <span>elapsed <b className="tabular-nums text-gray-900">{fmtDuration(elapsedMs)}</b></span>
              <span>stops automatically at <b className="tabular-nums text-gray-900">{capMinutes}m</b></span>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Link to={QUEUE_PATH} className="btn-cb-secondary">Go to the queue</Link>
              <button type="button" onClick={onNotifyMe} className="btn-cb-toolbar" disabled={notifyArmed}>
                {notifyArmed ? 'We’ll notify you' : 'Notify me when it’s ready'}
              </button>
              <button type="button" onClick={onStayAndWatch} className="btn-cb-toolbar">Stay and watch</button>
              <span className="flex-1" />
              {/* "Dismiss", never "Cancel" — DELETE does not stop the work. */}
              <button
                type="button"
                onClick={onDismissRun}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
              >
                Dismiss run
              </button>
            </div>
            {jobId && <p className="mt-2 text-[11px] text-gray-400">Job {String(jobId).slice(0, 8)}</p>}
          </div>
        </div>
      </div>

      {typicalHint && (
        <div className="flex items-center gap-2 border-t border-gray-100 bg-gray-50 px-6 py-2.5 sm:px-8">
          <InfoIcon className="w-3.5 h-3.5 shrink-0 text-gray-500" />
          <span className="text-[11.5px] text-gray-500">{typicalHint}</span>
        </div>
      )}
    </div>
  )
}

export default QueueHandoffPanel
